import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import useTranslations from '@/i18n/useTranslations';
import { getPublicGalleryQrVisits } from '@/services/galleryQrVisits.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import Loader from '@/shared-components/loader/Loader';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';

import GalleryVisitsEmptyState from './GalleryVisitsEmptyState';

import './styles/ProfileOverlayPopup.css';
import './styles/NotificationModal.css';
import '../gallery-visits.css';

export default function GalleryVisitsModal({ open, onClose, username, initialVisits = null }) {
  const t = useTranslations('common.profile.top_buttons.visited_galleries');
  const [items, setItems] = useState(() => (
    Array.isArray(initialVisits?.data) ? initialVisits.data : []
  ));
  const [nextCursor, setNextCursor] = useState(initialVisits?.next_cursor || null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const loadedUsernameRef = useRef(initialVisits ? username : null);

  useEffect(() => {
    if (!open || !username) return undefined;
    if (loadedUsernameRef.current === username) return undefined;

    let active = true;
    setLoading(true);
    setError('');
    setItems([]);
    setNextCursor(null);
    getPublicGalleryQrVisits(username)
      .then((result) => {
        if (!active) return;
        setItems(Array.isArray(result?.data) ? result.data : []);
        setNextCursor(result?.next_cursor || null);
        loadedUsernameRef.current = username;
      })
      .catch((requestError) => {
        if (active) setError(requestError?.message || t('error'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, username, t]);

  async function handleLoadMore() {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await getPublicGalleryQrVisits(username, { cursor: nextCursor });
      setItems((current) => [...current, ...(Array.isArray(result?.data) ? result.data : [])]);
      setNextCursor(result?.next_cursor || null);
    } catch (requestError) {
      setError(requestError?.message || t('error'));
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <GenericPopup
      open={open}
      className="profile-overlay-popup notifications-popup"
      opacity
      onBackgroundClick={onClose}
    >
      <div className="notifications-content__container gallery-visits-modal__container">
        <div className="notifications-content__header">
          <Text className="notifications-content__header-text">{t('title')}</Text>
          <XIcon className="notifications-content__header-close" onClick={onClose} />
        </div>

        {loading ? (
          <Loader className="gallery-visits-modal__loader" thin />
        ) : error ? (
          <Text className="gallery-visits-modal__message" paragraph>{error}</Text>
        ) : items.length === 0 ? (
          <GalleryVisitsEmptyState t={t} />
        ) : (
          <div className="gallery-visits-modal__list">
            {items.map((gallery) => (
              <Link
                key={gallery.id}
                className="gallery-visits-modal__item no-underline"
                to={`/user/${encodeURIComponent(gallery.user_username)}`}
                onClick={onClose}
              >
                <UserProfileImage
                  image={gallery.user_image?.image_id}
                  typeOfProfile="gallery"
                  className="gallery-visits-modal__image"
                />
                <span className="gallery-visits-modal__details">
                  <span className="gallery-visits-modal__name">{gallery.user_name || gallery.user_username}</span>
                  <span className="gallery-visits-modal__username">@{gallery.user_username}</span>
                  {gallery.location && <span className="gallery-visits-modal__location">{gallery.location}</span>}
                </span>
              </Link>
            ))}
            {nextCursor && (
              <ContinueButton
                className="gallery-visits-modal__load-more"
                loading={loadingMore}
                onClick={handleLoadMore}
              >
                {t('load_more')}
              </ContinueButton>
            )}
          </div>
        )}
      </div>
    </GenericPopup>
  );
}
